import { LineItem, Package } from '@vendasta/marketplace-packages';
import { App } from '@galaxy/marketplace-apps';
import { MenuItemEventType, MenuItemInterface } from './package-dropdown-item/package-dropdown-item.component';
import { ActivationStatus } from '@vendasta/sales-orders';
import { ActivationFailureDetails } from '@vendasta/order-fulfillment/lib/_internal/objects';

export class SalesPackage extends Package {
  salesProducts: SalesProduct[];
  addonsWithNoParent: SalesProduct[];
  totalAddons: number;
  activationStatus?: StatusNotificationItem;

  constructor(
    pkg: Package,
    salesProducts: SalesProduct[],
    addonsWithNoParent: SalesProduct[],
    totalAddons: number,
    activationStatus?: StatusNotificationItem,
  ) {
    super(pkg);
    this.salesProducts = salesProducts;
    this.addonsWithNoParent = addonsWithNoParent;
    this.totalAddons = totalAddons;
    this.activationStatus = activationStatus;
  }
}

export class SalesProduct extends App {
  activationStatus?: StatusNotificationItem;
  addons: SalesProduct[];
  menuItems: MenuItemInterface[] = [];

  constructor(app: App, addons: SalesProduct[], activationStatus?: StatusNotificationItem) {
    super(app);
    this.addons = addons;
    this.activationStatus = activationStatus;
  }
}

export interface PackageItemActivationStatus {
  packageItemId: string;
  status: ActivationStatus;
  uniqueId: string;
  editionId?: string;
  previousEditionId?: string;
  isEditionChange?: boolean;
  failureDetails?: string;
}

export interface StatusNotificationItem {
  icon: string;
  text: string;
  status: ActivationStatus;
  uniqueId: string;
  editionId?: string;
  previousEditionId?: string;
  isEditionChange?: boolean;
  failureDetails?: string;
}

export function buildSalesPackages(packages: Package[], apps: App[]): SalesPackage[] {
  if (!packages || packages.length < 1) {
    return [];
  }
  return packages.map((pkg) => buildSalesPackageFromPackage(pkg, apps));
}

function buildSalesPackageFromPackage(pkg: Package, apps: App[]): SalesPackage {
  if (!pkg) {
    return null;
  }

  const appMap: Map<string, App> = new Map<string, App>();
  (apps || []).forEach((app) => appMap.set(app.key.appId, app));

  const salesProducts = buildSalesProducts(pkg, appMap);
  // Addons can be in a package even if the parent product is not, this will create salesAddons with no parent
  const addonsWithNoParent: SalesProduct[] = buildSalesAddonsWithNoParent(pkg, appMap);

  const totalAddonsCount = (pkg.lineItems.lineItems || [])
    .filter((li) => !!li.parentId)
    .reduce((count, addon) => {
      return count + (addon.quantity || 1);
    }, 0);

  return new SalesPackage(pkg, salesProducts, addonsWithNoParent, totalAddonsCount);
}

export function buildSalesProducts(pkg: Package, appMap: Map<string, App>): SalesProduct[] {
  if (!(appMap && appMap.size && pkg.lineItems && (pkg.lineItems.lineItems || []).length)) {
    return [];
  }

  const productsInPackage = pkg.lineItems.lineItems.filter((li) => !li.parentId);
  return productsInPackage
    .map((li) => {
      const productMatch: App = appMap.get(li.id);
      const addonsInPackage = productMatch
        ? multiplyAddonsByTheirQuantity(
            pkg.lineItems.lineItems.filter((il) => il.parentId === productMatch.key.appId),
            appMap,
          )
        : [];
      return productMatch ? buildSalesProductFromProduct(productMatch, addonsInPackage) : null;
    })
    .filter((productItem) => productItem);
}

export function multiplyAddonsByTheirQuantity(addonLineItems: LineItem[], appMap: Map<string, App>): App[] {
  return addonLineItems.reduce((addonsArray, li) => {
    const addon = appMap.get(li.id);
    if (addon) {
      const quantity = li.quantity || 1;
      for (let i = 0; i < quantity; i++) {
        addonsArray.push(addon);
      }
    }
    return addonsArray;
  }, []);
}

export function buildSalesAddonsWithNoParent(pkg: Package, appMap: Map<string, App>): SalesProduct[] {
  if (!appMap || appMap.size < 1 || !pkg.lineItems || !(pkg.lineItems.lineItems || []).length) {
    return [];
  }
  const productsInPackageMap: Map<string, boolean> = new Map<string, boolean>();
  pkg.lineItems.lineItems.filter((li) => !li.parentId).forEach((li) => productsInPackageMap.set(li.id, true));

  const lineItemsThatAreAddonsThatDontHaveTheParentAppInThePackage = pkg.lineItems.lineItems.filter(
    (li) => !!li.parentId && !productsInPackageMap.get(li.parentId),
  );

  const addonArray: SalesProduct[] = [];
  lineItemsThatAreAddonsThatDontHaveTheParentAppInThePackage.map((li) => {
    const quantity = li.quantity || 1;
    const addon = appMap.get(li.id);
    if (quantity && addon) {
      for (let i = 0; i < quantity; i++) {
        addonArray.push(new SalesProduct(addon, []));
      }
    }
  });
  return addonArray;
}

export function buildSalesProductFromProduct(app: App, addons: App[]): SalesProduct {
  if (!app) {
    return null;
  }
  return new SalesProduct(
    app,
    (addons || []).map((a) => new SalesProduct(a, [])),
  );
}

export function addStatusesToSalesPackages(
  salesPackages: SalesPackage[],
  statuses: PackageItemActivationStatus[],
  failureDetails: ActivationFailureDetails[],
): SalesPackage[] {
  if (!statuses || statuses.length === 0) {
    return salesPackages;
  }

  return salesPackages.map((salesPackage) => {
    const newProducts = salesPackage.salesProducts.map((product) =>
      addStatusesToSalesProduct(product, statuses, failureDetails),
    );

    const newAddonsWithNoParent = salesPackage.addonsWithNoParent.map((addon) => {
      return addStatusesToSalesProduct(addon, statuses, failureDetails);
    });

    const productStatuses = newProducts.map((product) => product?.activationStatus?.status);
    const addonStatuses = newProducts
      .map((product) => (product?.addons ? product.addons.map((addon) => addon?.activationStatus?.status) : []))
      .reduce((statusList, reducedList) => reducedList.concat(statusList), []);
    const addonsWithNoParentStatuses = newAddonsWithNoParent.map((addon) =>
      addon?.activationStatus ? addon.activationStatus.status : null,
    );
    const allStatuses = [...productStatuses, ...addonStatuses, ...addonsWithNoParentStatuses];

    const packageStatus = aggregateStatuses(allStatuses);
    const statusNotification = createStatusNotificationItem(packageStatus, '');

    return new SalesPackage(
      salesPackage,
      newProducts,
      newAddonsWithNoParent,
      salesPackage.totalAddons,
      statusNotification,
    );
  });
}

export function addStatusesToSalesProduct(
  product: SalesProduct,
  statuses: PackageItemActivationStatus[],
  activationFailureDetails: ActivationFailureDetails[],
): SalesProduct {
  if (!statuses || statuses.length === 0) {
    return product;
  }
  const productStatus = findStatus(statuses, product.key.appId, product.key.editionId);
  if (!productStatus) {
    return product;
  }
  const statusNotification = createStatusNotificationItem(
    productStatus.status,
    productStatus.uniqueId,
    productStatus.editionId,
    productStatus.previousEditionId,
    productStatus.isEditionChange,
    productStatus.failureDetails,
  );
  if (!product.addons) {
    return new SalesProduct(product, [], statusNotification);
  }

  const newSalesAddons = product.addons.map((addon) => {
    return addStatusesToSalesProduct(addon, statuses, activationFailureDetails);
  });

  return new SalesProduct(product, newSalesAddons, statusNotification);
}

export function addMenuItemsToSalesPackages(salesPackages: SalesPackage[]): SalesPackage[] {
  return salesPackages.map((salesPackage) => {
    salesPackage.salesProducts = salesPackage.salesProducts.map((product) => {
      (product.addons || []).map((a) => addMenuItemsToSalesProduct(a));
      return addMenuItemsToSalesProduct(product);
    });
    salesPackage.addonsWithNoParent = salesPackage.addonsWithNoParent.map((a) => addMenuItemsToSalesProduct(a));
    return salesPackage;
  });
}

export function addMenuItemsToSalesProduct(product: SalesProduct): SalesProduct {
  if (
    product.activationStatus &&
    product.activationStatus.status === ActivationStatus.ERRORED &&
    !!product.activationStatus.uniqueId
  ) {
    product.menuItems = [
      {
        label: 'FRONTEND.SALES_UI.IGNORE_ERRORS',
        icon: '',
        value: product.activationStatus.uniqueId,
        eventType: MenuItemEventType.IGNORE_ACTIVATION_ERRORS,
      },
    ];
  }
  return product;
}

function findStatus(
  statuses: PackageItemActivationStatus[],
  id: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  editionId: string,
): PackageItemActivationStatus {
  if (!statuses || statuses.length <= 0) {
    return null;
  }
  const statusIndex = statuses.findIndex((s) => {
    // Temporary hack to get around issue reported on MT-1431
    // Package was edited after it was ordered, causing the editionId on the current package to be different than
    // the edition id on the package that was ordered. This causes the status to not be found. Checking only for the
    // item id shouldn't cause any issue since there won't be multiple activatable items with editions.
    return s.packageItemId === id /*&& (s.editionId || '') === (editionId || '')*/;
  });
  if (statusIndex < 0) {
    return null;
  }
  const status = statuses[statusIndex];
  statuses.splice(statusIndex, 1);
  return status;
}

function createStatusNotificationItem(
  status: ActivationStatus,
  uniqueId: string,
  editionId?: string,
  previousEditionId?: string,
  isEditionChange?: boolean,
  failureDetails?: string,
): StatusNotificationItem {
  switch (status) {
    case ActivationStatus.ACTIVATED:
      return {
        icon: 'check_circle',
        status: status,
        text: 'FRONTEND.SALES_UI.ACTIVATED',
        uniqueId: uniqueId,
        editionId,
        previousEditionId,
        isEditionChange,
        failureDetails: failureDetails,
      };
    case ActivationStatus.ALREADY_ACTIVATED:
      return {
        icon: 'warning',
        status: status,
        text: 'FRONTEND.SALES_UI.ACTIVATION_WARNINGS',
        uniqueId: uniqueId,
        editionId,
        previousEditionId,
        isEditionChange,
        failureDetails: failureDetails,
      };
    case ActivationStatus.IGNORED_ERRORS:
      return {
        icon: 'check_circle',
        status: status,
        text: 'FRONTEND.SALES_UI.ERRORS_IGNORED',
        uniqueId: uniqueId,
        editionId,
        previousEditionId,
        isEditionChange,
        failureDetails: failureDetails,
      };
    case ActivationStatus.ERRORED:
      return {
        icon: 'error',
        status: status,
        text: 'FRONTEND.SALES_UI.ACTIVATION_ERRORS',
        uniqueId: uniqueId,
        editionId,
        previousEditionId,
        isEditionChange,
        failureDetails: failureDetails,
      };
    default:
      return {
        icon: 'watch_later',
        status: status,
        text: 'FRONTEND.SALES_UI.PENDING_ACTIVATION',
        uniqueId: uniqueId,
        editionId,
        previousEditionId,
        isEditionChange,
        failureDetails: failureDetails,
      };
  }
}

function aggregateStatuses(statuses: ActivationStatus[]): ActivationStatus {
  const allStatuses = statuses || [];

  if (allStatuses.some((s) => s === ActivationStatus.ERRORED)) {
    return ActivationStatus.ERRORED;
  }

  if (allStatuses.some((s) => s === ActivationStatus.ALREADY_ACTIVATED)) {
    return ActivationStatus.ALREADY_ACTIVATED;
  }

  if (allStatuses.length > 0 && allStatuses.every((s) => s === ActivationStatus.PENDING || s === undefined)) {
    return ActivationStatus.PENDING;
  }

  return ActivationStatus.ACTIVATED;
}
